import type { SsoSignInErrorKind } from "../sso-sign-in-errors";

/** A tenant's configured OIDC provider, without the client secret. */
export type SsoProviderConfig = {
  providerId: string;
  /** The identity provider's discovery-document issuer, as stored (QL-619). */
  issuer: string;
  domain: string;
  clientId?: string;
  name?: string;
  /** Whether a client secret is stored. The secret itself is never returned (QL-618). */
  hasClientSecret: boolean;
};

/**
 * GET /internal/sso. `providerId` and `callbackUrl` are derived from the tenant
 * and known before SSO is configured, so the redirect URI can be registered in
 * the IdP first.
 */
export type SsoSettingsResponse = {
  providerId: string;
  callbackUrl: string;
  provider: SsoProviderConfig | null;
  /** Set while a slug rename waits for an administrator to confirm the new callback URL (QL-345). */
  pendingChange: SsoProviderIdChange | null;
};

/**
 * QL-345 — after a slug rename SSO keeps the provider id (and callback URL) of the
 * old slug. An administrator registers `newCallbackUrl` in the IdP and confirms;
 * after `confirmBy` the old slug can be claimed by another organization, which
 * moves SSO to the new id whether or not the IdP was updated.
 */
export type SsoProviderIdChange = {
  newProviderId: string;
  newCallbackUrl: string;
  confirmBy: string | null;
};

/**
 * Which callback URL a test sign-in goes through: the one SSO uses today, or the
 * pending new one after a slug rename (QL-345).
 */
export type SsoCallbackTestTarget = "current" | "pending";

/** RFC 6749 §4.1.2.1 and OIDC authentication errors an IdP may send back to the callback. */
export const SSO_AUTHORIZATION_ERROR_CODES = [
  "invalid_request",
  "unauthorized_client",
  "access_denied",
  "unsupported_response_type",
  "invalid_scope",
  "server_error",
  "temporarily_unavailable",
  "login_required",
  "consent_required",
  "interaction_required",
  "account_selection_required",
] as const;
export type SsoAuthorizationErrorCode = (typeof SSO_AUTHORIZATION_ERROR_CODES)[number];

/** RFC 6749 §5.2 token endpoint errors. */
export const SSO_TOKEN_ERROR_CODES = [
  "invalid_request",
  "invalid_client",
  "invalid_grant",
  "unauthorized_client",
  "unsupported_grant_type",
  "invalid_scope",
] as const;
export type SsoTokenErrorCode = (typeof SSO_TOKEN_ERROR_CODES)[number];

/**
 * `?callbackTestReason=` on the SSO settings page after a failed test sign-in
 * (QL-621): only these values are produced; any other IdP-supplied error text is
 * replaced by `idp_error` (authorization) or `token_http_<status>` (token).
 */
export type SsoCallbackTestReason =
  | "expired"
  | "wrong_callback"
  | "no_code"
  | "not_configured"
  | "idp_error"
  | "token_endpoint_not_allowed"
  | "token_request_failed"
  | "client_secret_undecryptable"
  | SsoAuthorizationErrorCode
  | SsoTokenErrorCode
  | `token_http_${number}`;

/** POST /internal/sso/test-sign-in */
export type SsoCallbackTestStartRequest = {
  target: SsoCallbackTestTarget;
};

/**
 * POST /internal/sso/test-sign-in — a test sign-in through the saved SSO
 * configuration that never creates a session or touches users. The browser goes
 * to `authorizationUrl`; the IdP sends it back to `redirectUri`, and the API lands
 * it on the SSO settings page with
 * `?callbackTest=success|failed&callbackTestTarget=<target>&callbackTestReason=<code>`
 * (and, on failure, `&callbackTestRef=<ref>` — QL-637 — the reference the settings
 * page looks up with `GET /internal/sso/sign-in-errors/:ref` to show the identity
 * provider's description).
 */
export type SsoCallbackTestStartResponse = {
  authorizationUrl: string;
  redirectUri: string;
};

/** `?ssoRef=` on the login page / `?callbackTestRef=` on the SSO settings page: the reference of a failed sign-in (QL-637). */
export type SsoSignInErrorSource = "login" | "callback_test";

/**
 * GET /internal/sso/sign-in-errors/:ref — what the API recorded for a failed SSO
 * sign-in or test sign-in in this tenant (QL-637). `description` is the identity
 * provider's `error_description`, sanitised to plain text and capped; null when
 * the provider sent none. Entries expire 7 days after the failure.
 */
export type SsoSignInErrorResponse = {
  ref: string;
  code: string;
  kind: SsoSignInErrorKind;
  description: string | null;
  /**
   * False when `description` arrived in the sign-in URL of a login anyone can
   * start for this provider (the IdP's front-channel `error_description`), so it
   * may have been written by whoever started that sign-in rather than by the
   * identity provider. True when QP received it server-to-server from the
   * identity provider, or through the admin's own test sign-in.
   */
  descriptionTrusted: boolean;
  providerId: string;
  source: SsoSignInErrorSource;
  /** ISO 8601 UTC. */
  at: string;
};

/**
 * PUT /internal/sso, saving the settings. `oidcClientSecret` omitted, empty or
 * whitespace-only keeps the secret already stored for the tenant's provider (QL-618,
 * "Leave blank to keep the existing secret"); a non-empty value replaces it (trimmed).
 * A provider created without a secret stores none.
 */
export type SsoProviderSaveRequest = {
  oidcIssuer: string;
  oidcClientId: string;
  oidcClientSecret?: string;
  oidcName?: string;
  /**
   * The discovered issuer the administrator confirmed (QL-619). Required when the
   * issuer the identity provider reports differs from `oidcIssuer`; bound to that
   * exact value, so a confirmation of another issuer does not apply.
   */
  confirmedIssuer?: string;
};

/** PUT /internal/sso — what was stored. */
export type SsoProviderUpdateResponse = {
  success: true;
  /** The `issuer` of the identity provider's OpenID configuration — the value ID tokens carry in `iss`. */
  issuer: string;
};

/**
 * PUT /internal/sso, HTTP 409: the identity provider reports an issuer other than the
 * one entered (trailing slash, Entra v1 host, ...). Nothing was stored; save again with
 * `confirmedIssuer = discoveredIssuer` to store the discovered value.
 */
export type SsoIssuerConfirmationRequiredResponse = {
  error: string;
  code: "issuer_confirmation_required";
  /** The URL entered, trimmed and without trailing slashes. */
  enteredIssuer: string;
  discoveredIssuer: string;
};

/** POST /internal/sso/test-connection */
export type SsoTestConnectionRequest = { issuerUrl?: string };

export type SsoTestConnectionResponse = {
  enteredIssuer: string;
  discoveredIssuer: string;
  /** Saving needs the administrator to confirm `discoveredIssuer`. */
  issuerDiffers: boolean;
  endpoints: { authorization: string | null; token: string | null; userinfo: string | null };
};

/**
 * PUT /internal/sso, removing the stored client secret (e.g. a move to a public PKCE
 * client). Secret-only: nothing else is read or changed, no discovery runs, and it
 * answers 409 if the provider was changed or deleted meanwhile. A non-blank
 * `oidcClientSecret` alongside it is a 400.
 */
export type SsoClientSecretRemoveRequest = {
  removeClientSecret: true;
  oidcClientSecret?: string;
};

export type SsoProviderUpdateRequest = SsoProviderSaveRequest | SsoClientSecretRemoveRequest;
